import { API } from '@/config';
import type { AccountResponse } from '@/features/auth/api';
import { AuthApiError } from '@/features/auth/api';

export type AccountListResponse = {
  accounts: AccountResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
};

export type AdminApiClient = {
  listUsers(page: number, limit: number, role?: string): Promise<AccountListResponse>;
  setUserRole(id: string, roleID: string): Promise<AccountResponse>;
};

export function createAdminApiClient(getToken: () => string | null): AdminApiClient {
  const base = API.wikiBase.replace(/\/+$/, '');

  return {
    listUsers(page, limit, role) {
      const token = requireToken(getToken());
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (role) params.set('role', role);

      return request<AccountListResponse>(`${base}/admin/users?${params}`, {
        headers: authorizedHeaders(token),
      });
    },

    setUserRole(id, roleID) {
      const token = requireToken(getToken());

      return request<AccountResponse>(`${base}/admin/users/${encodeURIComponent(id)}/role`, {
        method: 'PATCH',
        headers: { ...authorizedHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_id: roleID }),
      });
    },
  };
}

function requireToken(token: string | null): string {
  if (!token) {
    throw new AuthApiError('not authenticated', 401);
  }

  return token;
}

function authorizedHeaders(token: string): Record<string, string> {
  return { Accept: 'application/json', Authorization: `Bearer ${token}` };
}

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: unknown;
};

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    headers: { Accept: 'application/json', ...init?.headers },
    ...init,
  });

  const body = await parseBody(response);

  if (!response.ok) {
    throw new AuthApiError(errorMessage(body, response.statusText), response.status);
  }

  if (isEnvelope<T>(body)) {
    if (body.ok === false) {
      throw new AuthApiError(errorMessage(body, response.statusText), response.status);
    }

    if ('data' in body) {
      return body.data as T;
    }
  }

  return body as T;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isEnvelope<T>(body: unknown): body is ApiEnvelope<T> {
  return Boolean(body && typeof body === 'object' && 'ok' in body);
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: unknown }).error;

    if (typeof error === 'string' && error.trim()) return error;
  }

  return fallback || 'request failed';
}
